import api from "@/api/api";
import { toast } from "@/components/utils/ui_v4/alerts/toast/toast";
import { formatarValorMonetario } from "@/helpers";
import { computed, ref, watch } from "vue";
import * as XLSX from "xlsx";
import { parseDate } from "../utils/dateUtils";

export function useDetalhesCotacaoPropostaTab(props, context) {
  const SUPPLIER_COLOR_PALETTE = [
    "#3B82F6", "#22C55E", "#F59E0B", "#8B5CF6", "#14B8A6",
    "#F97316", "#EC4899", "#06B6D4", "#84CC16", "#6366F1",
    "#EF4444", "#0EA5E9", "#A855F7", "#10B981", "#EAB308",
    "#F43F5E", "#2DD4BF", "#4F46E5", "#FB7185", "#65A30D",
    "#0D9488", "#7C3AED", "#EA580C", "#0284C7", "#16A34A",
    "#CA8A04", "#C026D3", "#1D4ED8", "#059669", "#DC2626",
    "#7DD3FC", "#86EFAC", "#FCD34D", "#C4B5FD", "#67E8F9",
    "#FDBA74", "#F9A8D4", "#A7F3D0", "#93C5FD", "#D9F99D",
    "#FCA5A5", "#A5B4FC", "#5EEAD4", "#FDE68A", "#E9D5FF",
    "#BAE6FD", "#BBF7D0", "#FECACA", "#DDD6FE", "#99F6E4",
  ];

  const viewType = ref("item");

  const showItemsWithoutWinner = ref(false);
  const quickSelection = ref(null);
  const selectedSupplier = ref("todos");
  const maxPrice = ref("");
  const maxDelivery = ref("");
  const selectedFreightType = ref("todos");
  const selectedReliability = ref(null);
  const minCoverage = ref("");
  const hideOutOfTolerance = ref(false);
  const hideAboveReference = ref(false);

  // Estados de colapso dos cards
  const isQuickSelectionExpanded = ref(true);
  const isFiltersExpanded = ref(true);
  const isAdvancedOptionsExpanded = ref(true);

  const items = ref([]);
  const suppliers = ref([]);
  const currentIndex = ref(0);
  /** Prazo desejado (dias) — menor entre locais em cotacao_local_recebimento; usado na análise. */
  const prazoEntregaReferenciaDias = ref(null);
  /** Término do período de recebimento de propostas (cotacao_config) — string crua do banco. */
  const terminoPeriodoRecebimento = ref(null);

  const clearAllData = () => {
    items.value = [];
    suppliers.value = [];
    currentIndex.value = 0;
    prazoEntregaReferenciaDias.value = null;

    // Resetar seleções e filtros
    viewType.value = "item";
    showItemsWithoutWinner.value = false;
    quickSelection.value = null;
    selectedSupplier.value = "todos";
    maxPrice.value = "";
    maxDelivery.value = "";
    selectedFreightType.value = "todos";
    selectedReliability.value = null;
    minCoverage.value = "";
    hideOutOfTolerance.value = false;
    hideAboveReference.value = false;

    supplierOptions.value = [
      { text: "Todos", value: "todos" }
    ];
  };

  const toggleCard = (cardName) => {
    if (cardName === "quickSelection") {
      isQuickSelectionExpanded.value = !isQuickSelectionExpanded.value;
    } else if (cardName === "filters") {
      isFiltersExpanded.value = !isFiltersExpanded.value;
    } else if (cardName === "advancedOptions") {
      isAdvancedOptionsExpanded.value = !isAdvancedOptionsExpanded.value;
    }
  };

  const freightTypeOptions = [
    { text: "Todos", value: "todos" },
    { text: "CIF", value: "CIF" },
    { text: "FOB", value: "FOB" }
  ];

  const supplierOptions = ref([
    { text: "Todos", value: "todos" }
  ]);

  const fetchAnaliseProposta = async () => {
    try {
      if (!props.cotacaoId) {
        return;
      }

      items.value = [];

      const { data } = await api.get(`/cotacao/analise_proposta/${props.cotacaoId}`);

      if (data.success && data.produtos) {
        prazoEntregaReferenciaDias.value =
          data.prazo_entrega_referencia_dias != null && data.prazo_entrega_referencia_dias !== ""
            ? Number(data.prazo_entrega_referencia_dias)
            : null;

        terminoPeriodoRecebimento.value = data.termino_periodo_recebimento_proposta || null;

        items.value = data.produtos.map(p => {
          // Filtrar apenas fornecedores com status "Proposta enviada"
          const propostasEnviadas = p.fornecedores_participantes.filter(f => {
            const statusParticipacao = f.status_participacao || f.status_participacao_fornecedor || 'Proposta enviada';
            return statusParticipacao === 'Proposta enviada';
          });

          return {
            id: p.cotacao_produtos_id,
            productName: p.descricao_produto,
            code: p.cod_produto,
            lastPurchasePrice: p.preco_custo,
            prazoEntregaReferenciaDias:
              data.prazo_entrega_referencia_dias != null && data.prazo_entrega_referencia_dias !== ""
                ? Number(data.prazo_entrega_referencia_dias)
                : null,
            unit: p.idunidade_medida || '',
            desiredQuantity: p.qtd_pedida,
            minQtyAcceptedEmb: (() => {
              const v = p.qtd_minima_aceita_emb;
              if (v === null || v === undefined || v === "") return null;
              const n = Number(v);
              return isNaN(n) ? null : n;
            })(),
            maxQtyAcceptedEmb: (() => {
              const v = p.qtd_maxima_aceita_emb;
              if (v === null || v === undefined || v === "") return null;
              const n = Number(v);
              return isNaN(n) ? null : n;
            })(),
            proposalsCount: propostasEnviadas.length, // Contar apenas propostas enviadas
            proposals: propostasEnviadas.map(f => ({
              id: f.cotacao_fornecedores_participantes_id,
              supplierId: f.idfornecedor || null,
              supplierName: f.razao_social,
              cnpj: f.cnpj_formatado || f.cnpj,
              unitPrice: f.preco_unitario,
              totalCost: f.custo_total,
              offeredQuantity: f.qtd_ofertada != null ? Number(f.qtd_ofertada) : null,
              variacaoPct: f.variacao_pct != null ? f.variacao_pct : null,
              condicaoPagamentoLabel: f.condicao_pagamento_label || null,
              tipoFrete: f.tipo_frete != null ? f.tipo_frete : null,
              prazoFaturamento: f.prazo_faturamento != null ? f.prazo_faturamento : null,
              deliveryTime: f.prazo_entrega,
              reliability: f.confiabilidade || "Sem histórico",
              badges: f.indicadores ? f.indicadores.map(i => i.nome) : [],
              badgeList: f.indicadores
                ? f.indicadores.map(i => ({
                    nome: i.nome,
                    descricao: getSeloTooltip(i.nome, i.descricao),
                  }))
                : [],
              status: f.atendo_produto ? "Válida" : "Não atende",
              statusParticipacao: f.status_participacao || f.status_participacao_fornecedor || 'Proposta enviada',
              isWinner: !!f.ganhador
            }))
          };
        });

        // Atualizar supplierOptions (apenas fornecedores com proposta enviada)
        const suppliersList = new Set();
        data.produtos.forEach(p => {
          p.fornecedores_participantes
            .filter(f => {
              const statusParticipacao = f.status_participacao || f.status_participacao_fornecedor || 'Proposta enviada';
              return statusParticipacao === 'Proposta enviada';
            })
            .forEach(f => {
              suppliersList.add(f.razao_social);
            });
        });
        supplierOptions.value = [
          { text: "Todos", value: "todos" },
          ...Array.from(suppliersList).map(s => ({ text: s, value: s.toLowerCase() }))
        ];
      } else {
        items.value = [];
        prazoEntregaReferenciaDias.value = null;
      }
    } catch (error) {
      if (error.response && error.response.data.error === 'Cotação sem identificador externo') {
        toast.error("Análise de proposta não encontrada para esta cotação.");

        items.value = [];
        prazoEntregaReferenciaDias.value = null;
        return;
      }
      toast.error("Erro ao buscar análise de proposta!");

      items.value = [];
    }
  };

  const fetchAnalisePropostaPorFornecedor = async () => {
    try {
      if (!props.cotacaoId) return;

      suppliers.value = [];

      const { data } = await api.get(`/cotacao/analise_proposta_por_fornecedor/${props.cotacaoId}`);
      if (data.success && data.fornecedores) {
        if (data.prazo_entrega_referencia_dias != null && data.prazo_entrega_referencia_dias !== "") {
          prazoEntregaReferenciaDias.value = Number(data.prazo_entrega_referencia_dias);
        }
        if (data.termino_periodo_recebimento_proposta) {
          terminoPeriodoRecebimento.value = data.termino_periodo_recebimento_proposta;
        }
        suppliers.value = data.fornecedores.map(f => ({
          supplierName: f.razao_social,
          supplierId: f.fornecedor_id || null,
          cnpj: f.cnpj_formatado || f.cnpj,
          status: f.status_proposta,
          itemsTotal: f.itens_total != null ? f.itens_total : 0,
          itemsWinnerCount: f.itens_ganhador,
          totalValueOfItems: f.valor_total_todos_itens_formatado != null ? f.valor_total_todos_itens_formatado : f.valor_total_itens_ganhador_formatado,
          totalValueRaw: f.valor_total_todos_itens != null ? f.valor_total_todos_itens : 0,
          totalValueWinningItems: f.valor_total_itens_ganhador_formatado,
          tipoFrete: f.tipo_frete != null ? f.tipo_frete : null,
          freteAgregado: f.frete_agregado || null,
          prazoFaturamento: f.prazo_faturamento != null ? f.prazo_faturamento : null,
          prazoFaturamentoAgregado: f.prazo_faturamento_agregado || null,
          prazoEntrega: f.prazo_entrega != null ? f.prazo_entrega : null,
          prazoEntregaAgregado: f.prazo_entrega_agregado || null,
          deliveryTime: f.prazo_formatado,
          condicaoPagamentoAgregado: f.condicao_pagamento_agregado || null,
          precoVsReferencia: f.preco_vs_referencia || null,
          seloValorTotal: f.selo_valor_total
            ? { nome: f.selo_valor_total.nome, descricao: getSeloTooltip(f.selo_valor_total.nome, f.selo_valor_total.descricao) }
            : null,
          selosPrazoEntrega: Array.isArray(f.selos_prazo_entrega)
            ? f.selos_prazo_entrega.map(s => ({ nome: s.nome, descricao: getSeloTooltip(s.nome, s.descricao) }))
            : [],
          seloPrecoVsRef: f.preco_vs_referencia && f.preco_vs_referencia.selo
            ? { nome: f.preco_vs_referencia.selo.nome, descricao: getSeloTooltip(f.preco_vs_referencia.selo.nome, f.preco_vs_referencia.selo.descricao) }
            : null,
          badges: f.selos ? f.selos.map(s => s.nome) : [],
          selos: Array.isArray(f.selos)
            ? f.selos.map(s => ({
                nome: s.nome,
                descricao: getSeloTooltip(s.nome, s.descricao),
              }))
            : [],
        }));

      }
    } catch (error) {
      toast.error("Erro ao buscar análise de proposta por fornecedor!");

    }
  };

  watch(() => props.cotacaoId, async (newId) => {
    if (newId) {
      clearAllData();
      await fetchAnaliseProposta();
      await fetchAnalisePropostaPorFornecedor();
      await carregarAjustesAtivos();
    }
  }, { immediate: true });

  const parseValue = (val) => {
    if (typeof val === 'number' && Number.isFinite(val)) return val;
    if (typeof val !== 'string') return 0;
    const normalized = val
      .replace(/R\$\s*/i, '')
      .trim()
      .replace(/\./g, '')
      .replace(',', '.');
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n : 0;
  };

  watch(selectedFreightType, (newVal) => {
    const isFob = newVal === 'FOB';
    if (isFob && maxDelivery.value) {
      maxDelivery.value = "";
    }
  });

  const parseDelivery = (val) => {
    if (typeof val === 'number') return val;
    if (typeof val !== 'string') return 0;
    const match = val.match(/\d+/);
    const num = parseInt(match ? match[0] : '0');
    return isNaN(num) ? 999999 : num;
  };

  const SEL_TOOLTIP_FALLBACK = {
    "Abaixo do ref.":
      "Entre as propostas iguais ou abaixo do preço de referência, esta está entre a(s) de menor preço unitário no item (empate possível).",
    "Menor preço": "Esta é a proposta com o menor preço unitário para este item.",
    "Mais rápido":
      "Esta é a proposta com o menor prazo total (entrega + faturamento) para este item, entre as propostas comparáveis.",
    "Melhor vs prazo desejado":
      "Entre as propostas com prazo de entrega melhor que o desejado (não-FOB), esta está entre a(s) de menor prazo no item (empate possível).",
    "Pior vs prazo desejado":
      "Entre as propostas com prazo de entrega pior que o desejado (não-FOB), esta está entre a(s) de maior prazo no item (empate possível).",
    "Melhor vs último prazo":
      "O prazo médio de entrega proposto por este fornecedor é menor que o prazo desejado nos locais de recebimento. Itens FOB não entram na média do selo.",
    "Pior vs último prazo":
      "O prazo médio de entrega proposto por este fornecedor é maior que o prazo desejado nos locais de recebimento. Itens FOB não entram na média do selo.",
    "Acima do teto":
      "Esta proposta está acima do preço máximo aceitável definido para este item.",
    "Mais barato":
      "Este fornecedor tem o menor custo médio por item atendido: divide-se o Valor total exibido na coluna dele pelo número de Itens atendidos.",
    "Melhor vs último preço":
      "Compara o preço médio proposto por este fornecedor com o último preço de compra nos itens atendidos.",
    "Pior vs último preço":
      "Compara o preço médio proposto por este fornecedor com o último preço de compra nos itens atendidos.",
    "Fora da tolerância de qtd.":
      "A quantidade ofertada está fora da faixa mínima/máxima aceita em embalagem definida pelo comprador no item da cotação.",
    "Qtd. fora da tolerância":
      "A quantidade ofertada está fora da faixa mínima/máxima aceita em embalagem definida pelo comprador no item da cotação.",
    "Qtd. igual ao desejado":
      "A quantidade ofertada pelo fornecedor coincide com a quantidade pedida na cotação.",
  };

  function tooltipFallbackSelo(nome) {
    if (!nome || typeof nome !== "string") {
      return "Indicador calculado com base nas regras desta cotação e nas propostas recebidas.";
    }
    if (nome.indexOf("Acima do teto") === 0) {
      return SEL_TOOLTIP_FALLBACK["Acima do teto"];
    }
    return (
      SEL_TOOLTIP_FALLBACK[nome] ||
      "Indicador calculado com base nas regras desta cotação e nas propostas recebidas."
    );
  }

  const getSeloTooltip = (nome, descricao) => {
    if (descricao && String(descricao).trim()) {
      return String(descricao).trim();
    }
    return tooltipFallbackSelo(nome);
  };

  const isProposalValid = (p) => p && p.status === "Válida";

  const isFreteFob = (p) => {
    const t = p && p.tipoFrete != null ? String(p.tipoFrete).toUpperCase().trim() : "";
    return t === "FOB";
  };

  const proposalTotalCost = (p, item) => {
    if (!p) {
      return 0;
    }
    const tc = p.totalCost;
    const hasTotal =
      tc != null &&
      tc !== "" &&
      !(typeof tc === "string" && !String(tc).trim());
    if (hasTotal) {
      return parseValue(tc);
    }
    const unit = parseValue(p.unitPrice);
    const q = item ? Number(item.desiredQuantity) || 0 : 0;
    return unit * q;
  };

  const reliabilityRank = (p) => {
    const r = String(p.reliability || "").toLowerCase();
    if (r.includes("alta")) return 3;
    if (r.includes("média") || r.includes("media")) return 2;
    if (r.includes("baixa")) return 1;
    return 0;
  };

  const compareMenorCustoPorItem = (a, b, item) => {
    const ca = proposalTotalCost(a, item);
    const cb = proposalTotalCost(b, item);
    if (ca !== cb) return ca - cb;
    const ua = parseValue(a.unitPrice);
    const ub = parseValue(b.unitPrice);
    if (ua !== ub) return ua - ub;
    const da = parseDelivery(a.deliveryTime);
    const db = parseDelivery(b.deliveryTime);
    if (da !== db) return da - db;
    const ra = reliabilityRank(a);
    const rb = reliabilityRank(b);
    if (ra !== rb) return rb - ra;
    return String(a.supplierName).localeCompare(String(b.supplierName), "pt");
  };

  const compareMelhorPrazoPorItem = (a, b, item) => {
    const da = parseDelivery(a.deliveryTime);
    const db = parseDelivery(b.deliveryTime);
    if (da !== db) return da - db;
    const ca = proposalTotalCost(a, item);
    const cb = proposalTotalCost(b, item);
    if (ca !== cb) return ca - cb;
    const ra = reliabilityRank(a);
    const rb = reliabilityRank(b);
    if (ra !== rb) return rb - ra;
    return String(a.supplierName).localeCompare(String(b.supplierName), "pt");
  };

  const EPS = 1e-9;

  watch(quickSelection, (newVal) => {
    if (!newVal) return;

    const total = items.value.length;

    if (newVal === "menor_custo") {
      if (viewType.value === "item") {
        items.value.forEach((item) => {
          const valid = item.proposals.filter(isProposalValid);
          if (valid.length === 0) {
            item.proposals.forEach((p) => { p.isWinner = false; });
            return;
          }
          const best = [...valid].sort((a, b) => compareMenorCustoPorItem(a, b, item))[0];
          item.proposals.forEach((p) => {
            p.isWinner = isProposalValid(p) && p.id === best.id && p.supplierName === best.supplierName;
          });
        });
        items.value = [...items.value];
        const withW = items.value.filter((i) => i.proposals.some((p) => p.isWinner)).length;
        const without = total - withW;
        toast.success(
          `Seleção rápida aplicada (por item). ${withW} itens receberam ganhador nesta rodada. ${without} itens continuam sem ganhador.`
        );
        return;
      }

      const pendingItems = items.value.filter((item) => !item.proposals.some((p) => p.isWinner));
      if (pendingItems.length === 0) {
        toast.info("Não há itens pendentes sem ganhador.");
        return;
      }

      const supplierNames = new Set();
      pendingItems.forEach((item) => {
        item.proposals.filter(isProposalValid).forEach((p) => supplierNames.add(p.supplierName));
      });

      const candidates = [];
      supplierNames.forEach((name) => {
        let itensAtendidos = 0;
        let valorTotal = 0;
        pendingItems.forEach((item) => {
          const p = item.proposals.find((x) => x.supplierName === name && isProposalValid(x));
          if (p) {
            itensAtendidos += 1;
            valorTotal += proposalTotalCost(p, item);
          }
        });
        if (itensAtendidos === 0) return;
        candidates.push({
          name,
          itens_atendidos_pendentes: itensAtendidos,
          valor_total_pendente: valorTotal,
          custo_medio: valorTotal / itensAtendidos,
        });
      });

      if (candidates.length === 0) {
        toast.warning("Nenhuma proposta válida nos itens pendentes.");
        return;
      }

      candidates.sort((a, b) => {
        if (Math.abs(a.custo_medio - b.custo_medio) > EPS) return a.custo_medio - b.custo_medio;
        if (a.itens_atendidos_pendentes !== b.itens_atendidos_pendentes) {
          return b.itens_atendidos_pendentes - a.itens_atendidos_pendentes;
        }
        if (Math.abs(a.valor_total_pendente - b.valor_total_pendente) > EPS) {
          return a.valor_total_pendente - b.valor_total_pendente;
        }
        return String(a.name).localeCompare(String(b.name), "pt");
      });

      const chosen = candidates[0].name;
      let received = 0;
      pendingItems.forEach((item) => {
        const p = item.proposals.find((x) => x.supplierName === chosen && isProposalValid(x));
        if (p) {
          item.proposals.forEach((x) => {
            x.isWinner = x.id === p.id && x.supplierName === p.supplierName;
          });
          received += 1;
        }
      });
      items.value = [...items.value];
      const stillWithout = items.value.filter((i) => !i.proposals.some((x) => x.isWinner)).length;
      toast.success(
        `Seleção rápida aplicada (por fornecedor). ${received} itens receberam ganhador nesta rodada. ${stillWithout} itens continuam sem ganhador.`
      );
      return;
    }

    if (newVal === "melhor_prazo") {
      if (viewType.value === "item") {
        items.value.forEach((item) => {
          const valid = item.proposals.filter((p) => isProposalValid(p) && !isFreteFob(p));
          if (valid.length === 0) {
            item.proposals.forEach((p) => { p.isWinner = false; });
            return;
          }
          const best = [...valid].sort((a, b) => compareMelhorPrazoPorItem(a, b, item))[0];
          item.proposals.forEach((p) => {
            p.isWinner = isProposalValid(p) && p.id === best.id && p.supplierName === best.supplierName;
          });
        });
        items.value = [...items.value];
        const withW = items.value.filter((i) => i.proposals.some((p) => p.isWinner)).length;
        const without = total - withW;
        toast.success(
          `Seleção rápida aplicada (por item). ${withW} itens receberam ganhador nesta rodada. ${without} itens continuam sem ganhador.`
        );
        return;
      }

      const pendingItems = items.value.filter((item) => !item.proposals.some((p) => p.isWinner));
      if (pendingItems.length === 0) {
        toast.info("Não há itens pendentes sem ganhador.");
        return;
      }

      const supplierNames = new Set();
      pendingItems.forEach((item) => {
        item.proposals.filter(isProposalValid).forEach((p) => supplierNames.add(p.supplierName));
      });

      const eligible = [];
      supplierNames.forEach((name) => {
        let hasFob = false;
        for (const item of pendingItems) {
          const p = item.proposals.find((x) => x.supplierName === name && isProposalValid(x));
          if (p && p.tipoFrete && String(p.tipoFrete).toUpperCase() === "FOB") {
            hasFob = true;
            break;
          }
        }
        if (hasFob) return;

        let sumPrazo = 0;
        let count = 0;
        let valorTotal = 0;
        pendingItems.forEach((item) => {
          const p = item.proposals.find((x) => x.supplierName === name && isProposalValid(x));
          if (p) {
            sumPrazo += parseDelivery(p.deliveryTime);
            count += 1;
            valorTotal += proposalTotalCost(p, item);
          }
        });
        if (count === 0) return;
        eligible.push({
          name,
          prazo_medio: sumPrazo / count,
          itens_atendidos_pendentes: count,
          valor_total_pendente: valorTotal,
          custo_medio: valorTotal / count,
        });
      });

      if (eligible.length === 0) {
        toast.warning(
          "Não é possível aplicar melhor prazo (por fornecedor): nenhum fornecedor elegível (FOB em item pendente exclui o fornecedor) ou sem propostas válidas."
        );
        return;
      }

      const minPrazo = Math.min(...eligible.map((e) => e.prazo_medio));
      const tied = eligible.filter((e) => Math.abs(e.prazo_medio - minPrazo) <= EPS);
      if (tied.length > 1) {
        toast.warning(
          "Empate na menor média de prazo entre fornecedores. Ajuste manualmente ou use outro critério."
        );
        return;
      }

      const winner = tied[0];
      let received = 0;
      pendingItems.forEach((item) => {
        const p = item.proposals.find((x) => x.supplierName === winner.name && isProposalValid(x));
        if (p) {
          item.proposals.forEach((x) => {
            x.isWinner = x.id === p.id && x.supplierName === p.supplierName;
          });
          received += 1;
        }
      });
      items.value = [...items.value];
      const stillWithout = items.value.filter((i) => !i.proposals.some((x) => x.isWinner)).length;
      toast.success(
        `Seleção rápida aplicada (por fornecedor). ${received} itens receberam ganhador nesta rodada. ${stillWithout} itens continuam sem ganhador.`
      );
    }
  });



  const itemsWithWinner = computed(() => {
    return items.value.filter(item => item.proposals.some(p => p.isWinner)).length;
  });

  const totalItems = computed(() => items.value.length);

  const itemsWithoutWinner = computed(() => totalItems.value - itemsWithWinner.value);
  const hasItensRestantesSemGanhador = computed(() => itemsWithoutWinner.value > 0);

  const winnerDistribution = computed(() => {
    const total = totalItems.value || 0;

    const supplierOrder = [];
    suppliersView.value.forEach((supplier) => {
      if (supplier && supplier.supplierName && !supplierOrder.includes(supplier.supplierName)) {
        supplierOrder.push(supplier.supplierName);
      }
    });

    // Fallback quando lista de fornecedores não estiver pronta
    if (supplierOrder.length === 0) {
      items.value.forEach((item) => {
        item.proposals.forEach((proposal) => {
          if (proposal && proposal.supplierName && !supplierOrder.includes(proposal.supplierName)) {
            supplierOrder.push(proposal.supplierName);
          }
        });
      });
    }

    const winnerCountBySupplier = {};
    items.value.forEach((item) => {
      const winner = item.proposals.find((p) => p.isWinner);
      if (winner && winner.supplierName) {
        winnerCountBySupplier[winner.supplierName] = (winnerCountBySupplier[winner.supplierName] || 0) + 1;
      }
    });

    const segments = supplierOrder
      .map((supplierName, index) => {
        const count = winnerCountBySupplier[supplierName] || 0;
        return {
          supplierName,
          count,
          color: SUPPLIER_COLOR_PALETTE[index % SUPPLIER_COLOR_PALETTE.length],
          percent: total > 0 ? (count / total) * 100 : 0,
        };
      })
      .filter((segment) => segment.count > 0);

    const semGanhador = Math.max(0, itemsWithoutWinner.value);

    const chips = [
      ...segments.map((segment) => ({
        key: `supplier-${segment.supplierName}`,
        label: `${segment.supplierName} · ${segment.count}`,
        color: segment.color,
      })),
      {
        key: "sem-ganhador",
        label: `Sem ganhador · ${semGanhador}`,
        color: "#9CA3AF",
      },
    ];

    return {
      segments,
      semGanhador,
      semGanhadorPercent: total > 0 ? (semGanhador / total) * 100 : 0,
      chips,
    };
  });

  const totalCost = computed(() => {
    return items.value.reduce((total, item) => {
      const winner = item.proposals.find(p => p.isWinner);
      if (winner) {
        return total + parseValue(winner.totalCost);
      }
      return total;
    }, 0);
  });

  const winningSuppliers = computed(() => {
    const winners = new Set();
    items.value.forEach(item => {
      item.proposals.forEach(p => {
        if (p.isWinner) winners.add(p.supplierName);
      });
    });
    return winners.size;
  });

  const mainSupplier = computed(() => {
    const supplierCounts = {};
    items.value.forEach(item => {
      const winner = item.proposals.find(p => p.isWinner);
      if (winner) {
        supplierCounts[winner.supplierName] = (supplierCounts[winner.supplierName] || 0) + 1;
      }
    });

    let maxCount = 0;
    let main = "Nenhum";
    for (const [supplier, count] of Object.entries(supplierCounts)) {
      if (count > maxCount) {
        maxCount = count;
        main = supplier;
      }
    }
    return main;
  });

  const currentItem = computed(() => filteredItems.value[currentIndex.value]);

  const hasProposals = computed(() => {
    return filteredItems.value.length > 0;
  });

  const suppliersView = computed(() => {
    // Usar items.value (lista completa) para estado de ganhador, assim hasPartialWinner e rótulos dos botões não dependem de filtros
    const itemsForWinnerState = items.value;
    if (suppliers.value.length > 0) {
      return suppliers.value.map(s => {
        const offeredItems = itemsForWinnerState.filter(item =>
          item.proposals.some(p => p.supplierName === s.supplierName)
        );
        const wonItems = itemsForWinnerState.filter(item =>
          item.proposals.some(p => p.supplierName === s.supplierName && p.isWinner)
        );

        const totalItemsCount = itemsForWinnerState.length;
        const itemsAtendidos = s.itemsTotal != null ? s.itemsTotal : 0;
        const itemsNotAttended = Math.max(0, totalItemsCount - itemsAtendidos);
        const naoAtendeNenhumItem = totalItemsCount > 0 && itemsAtendidos === 0;
        const isPartialWinner = itemsNotAttended > 0 && wonItems.length > 0;
        // Existe outro fornecedor que é ganhador parcial? (tem vitórias mas não atende todos os itens)
        const hasOtherPartialWinner = suppliers.value.some(s2 => s2.supplierName !== s.supplierName && (totalItemsCount - (s2.itemsTotal != null ? s2.itemsTotal : 0)) > 0 && itemsForWinnerState.some(it => it.proposals.some(p => p.supplierName === s2.supplierName && p.isWinner)));
        const isWinnerPendingItems = hasOtherPartialWinner && !isPartialWinner && !(offeredItems.length > 0 && wonItems.length === offeredItems.length) && wonItems.length > 0;
        return {
          ...s,
          id: `supplier-${s.supplierName}-${(s.cnpj || '').toString().replace(/\s/g, '')}`,
          itemsTotal: s.itemsTotal,
          itemsWinnerCount: wonItems.length,
          itemsNotAttended,
          naoAtendeNenhumItem,
          isPartialWinner,
          isWinnerPendingItems,
          totalValueOfItems: s.totalValueOfItems,
          isTotalWinner: offeredItems.length > 0 && wonItems.length === offeredItems.length
        };
      }).filter(s => s.itemsWinnerCount > 0 || itemsForWinnerState.some(item => item.proposals.some(p => p.supplierName === s.supplierName)));
    }

    const map = new Map();
    const totalItemsCount = itemsForWinnerState.length;

    itemsForWinnerState.forEach((item) => {
      item.proposals.forEach((p) => {
        const key = `${p.supplierName}-${p.cnpj}`;
        const entry = map.get(key) || {
          id: key,
          supplierName: p.supplierName,
          cnpj: p.cnpj,
          status: p.status,
          badges: new Set(),
          itemsWinnerCount: 0,
          totalValueOfItems: 0,
          deliveryTime: p.deliveryTime,
          reliability: p.reliability,
          totalOfferedItems: 0,
          itemsAtende: 0,
        };

        entry.totalOfferedItems += 1;
        if (p.status === "Válida") entry.itemsAtende += 1;

        if (Array.isArray(p.badges)) {
          p.badges.forEach((b) => entry.badges.add(b));
        }

        if (p.isWinner) {
          entry.itemsWinnerCount += 1;
          entry.totalValueOfItems += parseValue(p.totalCost);
        }

        map.set(key, entry);
      });
    });

    const allEntries = Array.from(map.values());
    return allEntries.map((e) => {
      const itemsAtende = e.itemsAtende != null ? e.itemsAtende : (e.totalOfferedItems || 0);
      const itemsNotAttended = Math.max(0, totalItemsCount - itemsAtende);
      const naoAtendeNenhumItem =
        totalItemsCount > 0 && (e.itemsAtende != null ? e.itemsAtende : 0) === 0;
      const isPartialWinner = itemsNotAttended > 0 && e.itemsWinnerCount > 0;
      const hasOtherPartialWinner = allEntries.some(o => o.supplierName !== e.supplierName && (totalItemsCount - (o.itemsAtende != null ? o.itemsAtende : o.totalOfferedItems || 0)) > 0 && o.itemsWinnerCount > 0);
      const isWinnerPendingItems = hasOtherPartialWinner && !isPartialWinner && !(e.totalOfferedItems > 0 && e.itemsWinnerCount === e.totalOfferedItems) && e.itemsWinnerCount > 0;
      return {
        ...e,
        itemsTotal: e.totalOfferedItems || 0,
        itemsNotAttended,
        naoAtendeNenhumItem,
        isPartialWinner,
        isWinnerPendingItems,
        badges: Array.from(e.badges),
        winnerPercent: totalItemsCount > 0 ? Math.round((e.itemsWinnerCount * 100) / totalItemsCount) : 0,
        isTotalWinner: e.totalOfferedItems > 0 && e.itemsWinnerCount === e.totalOfferedItems,
      };
    });
  });

  const hasPartialWinner = computed(() =>
    (suppliersView.value || []).some(s => s.isPartialWinner)
  );

  const activeFilters = computed(() => {
    const filters = [];

    // Só adiciona se realmente selecionado (não null)
    if (quickSelection.value === "menor_custo") filters.push("Menor custo total");
    if (quickSelection.value === "melhor_prazo") filters.push("Melhor prazo de entrega");



    if (selectedReliability.value === "alta") filters.push("Confiabilidade Alta");
    if (selectedReliability.value === "media") filters.push("Confiabilidade Média");
    if (selectedReliability.value === "baixa") filters.push("Confiabilidade Baixa");
    if (selectedReliability.value === "sem_historico") filters.push("Sem histórico");

    if (showItemsWithoutWinner.value) filters.push("Apenas itens sem ganhador");

    if (selectedSupplier.value && selectedSupplier.value !== "todos") {
      const supplier = supplierOptions.value.find(s => s.value === selectedSupplier.value);
      if (supplier) filters.push(`Fornecedor: ${supplier.text}`);
    }

    if (selectedFreightType.value && selectedFreightType.value !== "todos") {
      filters.push(`Frete: ${selectedFreightType.value}`);
    }

    if (maxPrice.value) filters.push(`Preço máx: R$ ${maxPrice.value}`);
    if (maxDelivery.value) filters.push(`Prazo máx: ${maxDelivery.value} dias`);
    if (minCoverage.value) filters.push(`Cobertura mín: ${minCoverage.value}%`);
    if (hideOutOfTolerance.value) filters.push("Ocultar fora da tolerância");
    if (hideAboveReference.value) filters.push("Ocultar acima de 20% do ref.");

    return filters;
  });

  const filteredItems = computed(() => {
    let result = items.value;

    const hasSupplierFilter = selectedSupplier.value && selectedSupplier.value !== "todos";
    const hasPriceFilter = !!maxPrice.value;
    const hasDeliveryFilter = !!maxDelivery.value;
    const hasFreightFilter = selectedFreightType.value && selectedFreightType.value !== "todos";
    const hasItemsWithoutWinnerFilter = showItemsWithoutWinner.value;

    if (hasItemsWithoutWinnerFilter) {
      result = result.filter(item => !item.proposals.some(p => !!p.isWinner));
    }

    if (hasSupplierFilter || hasPriceFilter || hasDeliveryFilter || hasFreightFilter || hideAboveReference.value || hideOutOfTolerance.value) {
      result = result.map(item => {
        const filteredProposals = item.proposals.filter(p => {
          let match = true;

          if (hasSupplierFilter) {
            match = match && p.supplierName.toLowerCase() === selectedSupplier.value.toLowerCase();
          }

          if (hasPriceFilter) {
            const max = parseFloat(maxPrice.value.replace(',', '.'));
            if (!isNaN(max)) {
              const val = typeof p.unitPrice === 'string'
                ? parseFloat(p.unitPrice.replace('R$ ', '').replace(/\./g, '').replace(',', '.'))
                : p.unitPrice;
              match = match && (val <= max);
            }
          }

          if (hasDeliveryFilter) {
            const max = parseInt(maxDelivery.value);
            if (!isNaN(max)) {
              const delivery = parseInt(p.deliveryTime);
              match = match && (!isNaN(delivery) && delivery <= max);
            }
          }

          if (hasFreightFilter) {
            match = match && p.tipoFrete && p.tipoFrete.toUpperCase() === selectedFreightType.value.toUpperCase();
          }

          if (hideAboveReference.value && p.variacaoPct != null && p.variacaoPct > 20) {
            match = false;
          }

          if (hideOutOfTolerance.value && p.offeredQuantity != null) {
            const desired = Number(item.desiredQuantity) || 0;
            if (desired > 0 && p.offeredQuantity !== desired) {
              match = false;
            }
          }

          return match;
        });

        return {
          ...item,
          proposals: filteredProposals
        };
      }).filter(item => item.proposals.length > 0);
    }

    return result;
  });

  watch(filteredItems, (newVal) => {
    if (currentIndex.value >= newVal.length) {
      currentIndex.value = Math.max(0, newVal.length - 1);
    }
  });

  const buildFreteAgregadoCliente = (cif, fob) => {
    const total = cif + fob;
    if (total === 0) return null;
    let texto = "–";
    if (fob === 0) texto = "CIF";
    else if (cif === 0) texto = "FOB";
    else {
      const predominante = cif >= fob ? "CIF" : "FOB";
      const pctPred = Math.round((Math.max(cif, fob) / total) * 100);
      texto = `Misto | ${pctPred}% ${predominante}`;
    }
    const cifPct = total > 0 ? Math.round((cif / total) * 100) : 0;
    const fobPct = total > 0 ? Math.round((fob / total) * 100) : 0;
    return {
      texto,
      tooltip: `Distribuição do tipo de frete nos itens atendidos:\nCIF: ${cifPct}% (${cif} itens)\nFOB: ${fobPct}% (${fob} itens)`,
      cif_count: cif,
      fob_count: fob,
      cif_pct: cifPct,
      fob_pct: fobPct,
    };
  };

  const calcStatsCliente = (vals) => {
    if (!vals || vals.length === 0) return null;
    const sorted = [...vals].sort((a, b) => a - b);
    const n = sorted.length;
    const sum = sorted.reduce((a, b) => a + b, 0);
    const media = Math.round((sum / n) * 10) / 10;
    const mediana = n % 2 === 0
      ? Math.round(((sorted[n / 2 - 1] + sorted[n / 2]) / 2) * 10) / 10
      : sorted[Math.floor(n / 2)];
    return {
      min: sorted[0],
      media,
      mediana,
      max: sorted[n - 1],
    };
  };

  const buildCondicaoAgregadoCliente = (condicaoMap, itensTotal) => {
    const entries = Object.entries(condicaoMap);
    if (entries.length === 0) {
      return {
        texto: "–",
        tooltip: "Nenhuma condição de pagamento informada nos itens atendidos.",
        distribuicao: {},
      };
    }
    if (entries.length === 1) {
      const label = entries[0][0];
      return { texto: label, tooltip: label, distribuicao: { ...condicaoMap } };
    }
    entries.sort((a, b) => b[1] - a[1]);
    const primLabel = entries[0][0];
    const primCount = entries[0][1];
    const primPct = itensTotal > 0 ? Math.round((primCount / itensTotal) * 100) : 0;
    const texto = `Misto | ${primLabel} (${primPct}% itens)`;
    const linhas = entries.map(([label, cnt]) => {
      const pct = itensTotal > 0 ? Math.round((cnt / itensTotal) * 100) : 0;
      return `${label}: ${pct}% (${cnt} itens)`;
    });
    return {
      texto,
      tooltip: `Distribuição das condições nos itens atendidos:\n${linhas.join("\n")}`,
      distribuicao: Object.fromEntries(entries),
    };
  };

  const enrichSupplierFromFilteredItems = (supplier, fi) => {
    const name = supplier.supplierName;
    let atendidos = 0;
    let ganhos = 0;
    let totalValue = 0;
    let cif = 0;
    let fob = 0;
    const prEnt = [];
    const prFat = [];
    const condMap = {};
    const deltas = [];

    for (const item of fi) {
      const p = item.proposals.find((x) => x.supplierName === name);
      if (!p) continue;
      if (isProposalValid(p)) {
        atendidos++;
        totalValue += proposalTotalCost(p, item);
        const tf = String(p.tipoFrete || "").toUpperCase().trim();
        if (tf === "CIF") cif++;
        else if (tf === "FOB") fob++;
        const pe = p.deliveryTime != null ? parseDelivery(p.deliveryTime) : null;
        if (pe != null && pe > 0 && pe < 999999) prEnt.push(pe);
        const pf = p.prazoFaturamento != null ? Number(p.prazoFaturamento) : null;
        if (pf != null && pf > 0 && !Number.isNaN(pf)) prFat.push(pf);
        if (p.condicaoPagamentoLabel) {
          condMap[p.condicaoPagamentoLabel] = (condMap[p.condicaoPagamentoLabel] || 0) + 1;
        }
        if (p.variacaoPct != null && !Number.isNaN(Number(p.variacaoPct))) {
          deltas.push(Number(p.variacaoPct));
        }
      }
      if (p.isWinner && isProposalValid(p)) {
        ganhos++;
      }
    }

    const universe = fi.length;
    const itemsNotAttended = Math.max(0, universe - atendidos);

    const freteAgregado = buildFreteAgregadoCliente(cif, fob);
    const prazoEntregaAgregado = calcStatsCliente(prEnt);
    const prazoFaturamentoAgregado = calcStatsCliente(prFat);
    const condicaoPagamentoAgregado = buildCondicaoAgregadoCliente(condMap, atendidos > 0 ? atendidos : universe);

    let precoVsReferencia = supplier.precoVsReferencia;
    let seloPrecoVsRef = supplier.seloPrecoVsRef;
    if (deltas.length > 0) {
      const mediaDelta = Math.round((deltas.reduce((a, b) => a + b, 0) / deltas.length) * 10) / 10;
      let selo = null;
      const descSeloRef =
        (supplier.precoVsReferencia &&
          supplier.precoVsReferencia.selo &&
          supplier.precoVsReferencia.selo.descricao) ||
        "";
      if (mediaDelta < 0) {
        selo = { nome: "Melhor vs último preço", descricao: descSeloRef };
      } else if (mediaDelta > 0) {
        selo = { nome: "Pior vs último preço", descricao: descSeloRef };
      }
      precoVsReferencia = {
        media_delta_pct: mediaDelta,
        texto: `Média ${mediaDelta >= 0 ? "+" : ""}${mediaDelta}%`,
        selo,
      };
      seloPrecoVsRef = selo
        ? { nome: selo.nome, descricao: getSeloTooltip(selo.nome, selo.descricao) }
        : null;
    }

    const totalValueFormatted = formatarValorMonetario(totalValue);

    const naoAtendeNenhumItem = universe > 0 && atendidos === 0;

    const freteFallback = atendidos === 0 ? null : supplier.freteAgregado;
    const tipoFreteFallback = atendidos === 0 ? "–" : supplier.tipoFrete;

    return {
      ...supplier,
      itemsTotal: atendidos,
      itemsNotAttended,
      itemsWinnerCount: ganhos,
      totalValueOfItems: totalValueFormatted,
      totalValueRaw: totalValue,
      tipoFrete: freteAgregado ? freteAgregado.texto : tipoFreteFallback,
      freteAgregado: freteAgregado || freteFallback,
      prazoEntregaAgregado: prazoEntregaAgregado || supplier.prazoEntregaAgregado,
      prazoFaturamentoAgregado: prazoFaturamentoAgregado || supplier.prazoFaturamentoAgregado,
      prazoEntrega: prazoEntregaAgregado ? Math.round(prazoEntregaAgregado.media) : supplier.prazoEntrega,
      prazoFaturamento: prazoFaturamentoAgregado ? Math.round(prazoFaturamentoAgregado.media) : supplier.prazoFaturamento,
      deliveryTime: prazoEntregaAgregado ? `média ${prazoEntregaAgregado.media} dias` : supplier.deliveryTime,
      condicaoPagamentoAgregado: condicaoPagamentoAgregado || supplier.condicaoPagamentoAgregado,
      precoVsReferencia,
      seloPrecoVsRef,
      naoAtendeNenhumItem,
      selosPrazoEntrega: [],
    };
  };

  const filteredSuppliersView = computed(() => {
    const fi = filteredItems.value;
    let list = suppliersView.value
      .map((s) => enrichSupplierFromFilteredItems(s, fi))
      .filter((s) =>
        fi.some((item) => item.proposals.some((p) => p.supplierName === s.supplierName))
      );

    if (minCoverage.value) {
      const minPct = parseFloat(minCoverage.value);
      if (!isNaN(minPct) && minPct > 0) {
        const totalItemsCount = items.value.length;
        list = list.filter((s) => {
          const itemsAtende = s.itemsTotal != null ? s.itemsTotal : 0;
          const coveragePct = totalItemsCount > 0 ? (itemsAtende / totalItemsCount) * 100 : 0;
          return coveragePct >= minPct;
        });
      }
    }

    const custosMedios = {};
    list.forEach((s) => {
      const it = s.itemsTotal != null ? s.itemsTotal : 0;
      if (it > 0 && s.totalValueRaw != null) {
        custosMedios[s.supplierName] = s.totalValueRaw / it;
      }
    });
    const cmVals = Object.values(custosMedios);
    let seloMaisBaratoName = null;
    if (cmVals.length > 1) {
      const minC = Math.min(...cmVals);
      const winners = Object.keys(custosMedios).filter(
        (k) => Math.abs(custosMedios[k] - minC) < 0.01
      );
      if (winners.length < Object.keys(custosMedios).length) {
        seloMaisBaratoName = winners[0];
      }
    }

    const mediasPrazo = list
      .map((s) => ({
        name: s.supplierName,
        m: s.prazoEntregaAgregado && s.prazoEntregaAgregado.media,
      }))
      .filter((x) => x.m != null && x.m > 0);
    let nomesMaisRapido = new Set();
    if (mediasPrazo.length > 1) {
      const minP = Math.min(...mediasPrazo.map((x) => x.m));
      const w = mediasPrazo.filter((x) => Math.abs(x.m - minP) < 0.01);
      if (w.length < mediasPrazo.length) {
        nomesMaisRapido = new Set(w.map((x) => x.name));
      }
    }

    const refDias = prazoEntregaReferenciaDias.value;
    /** Comparação de médias agregadas (dias); margem para float */
    const epsPrazoAgreg = 0.05;

    // Melhor / Pior vs prazo desejado (visão agregada): só quem empata na melhor/pior média entre fornecedores com itens atendidos.
    // Itens não atendidos já não entram em prazoEntregaAgregado / média (enrichSupplierFromFilteredItems).
    let nomesMelhorVsPrazoDesejadoAgreg = new Set();
    let nomesPiorVsPrazoDesejadoAgreg = new Set();
    if (refDias != null) {
      const comMediaValida = list
        .map((s) => ({
          name: s.supplierName,
          m: s.prazoEntregaAgregado && s.prazoEntregaAgregado.media,
          it: s.itemsTotal != null ? s.itemsTotal : 0,
        }))
        .filter((x) => x.m != null && x.m > 0 && x.it > 0);

      const poolMelhor = comMediaValida.filter((x) => x.m < refDias);
      if (poolMelhor.length > 0) {
        const minM = Math.min(...poolMelhor.map((x) => x.m));
        poolMelhor
          .filter((x) => Math.abs(x.m - minM) < epsPrazoAgreg)
          .forEach((x) => nomesMelhorVsPrazoDesejadoAgreg.add(x.name));
      }

      const poolPior = comMediaValida.filter((x) => x.m > refDias);
      if (poolPior.length > 0) {
        const maxM = Math.max(...poolPior.map((x) => x.m));
        poolPior
          .filter((x) => Math.abs(x.m - maxM) < epsPrazoAgreg)
          .forEach((x) => nomesPiorVsPrazoDesejadoAgreg.add(x.name));
      }
    }

    const descMelhorPrazoAgreg =
      "Visão agregada: entre os fornecedores visíveis com pelo menos um item atendido, a média de prazo de entrega deste fornecedor está entre as menores entre as que ficam abaixo do prazo desejado da cotação. Itens não atendidos não entram na média. Empate: vários fornecedores podem exibir o selo.";
    const descPiorPrazoAgreg =
      "Visão agregada: entre os fornecedores visíveis com pelo menos um item atendido, a média de prazo de entrega deste fornecedor está entre as maiores entre as que ficam acima do prazo desejado da cotação. Itens não atendidos não entram na média. Empate: vários fornecedores podem exibir o selo.";

    return list.map((s) => {
      const selosPrazo = [];
      if (nomesMaisRapido.has(s.supplierName)) {
        selosPrazo.push({
          nome: "Mais rápido",
          descricao: getSeloTooltip("Mais rápido", ""),
        });
      }
      if (nomesMelhorVsPrazoDesejadoAgreg.has(s.supplierName)) {
        selosPrazo.push({
          nome: "Melhor vs prazo desejado",
          descricao: getSeloTooltip("Melhor vs prazo desejado", descMelhorPrazoAgreg),
        });
      } else if (nomesPiorVsPrazoDesejadoAgreg.has(s.supplierName)) {
        selosPrazo.push({
          nome: "Pior vs prazo desejado",
          descricao: getSeloTooltip("Pior vs prazo desejado", descPiorPrazoAgreg),
        });
      }

      return {
        ...s,
        seloValorTotal:
          seloMaisBaratoName === s.supplierName
            ? {
                nome: "Mais barato",
                descricao: getSeloTooltip(
                  "Mais barato",
                  "Menor custo médio por item atendido entre os fornecedores visíveis com os filtros atuais."
                ),
              }
            : null,
        selosPrazoEntrega: selosPrazo,
      };
    });
  });

  const isButtonsDisabled = computed(() => {
    const isConcludedOrCancelled = props.statusCotacao === "Concluída" || props.statusCotacao === "Cancelada";

    // Verificar se o prazo expirou
    let isExpired = false;
    if (props.terminoRecebimento) {
      const termDate = parseDate(props.terminoRecebimento);
      if (termDate && new Date() > termDate) {
        isExpired = true;
      }
    }

    return isConcludedOrCancelled || isExpired;
  });


  const formatQuantity = (value) => {
    if (!value) return value;
    const num = parseFloat(value);
    // Remove casas decimais desnecessárias (ex: 10.0000 vira 10, mas 10.5 continua 10.5)
    return num % 1 === 0 ? num.toFixed(0) : num.toString();
  };

  const formatLastPurchasePrice = (value) => {
    if (value === null || value === undefined || value === '') return '–';
    const num = parseFloat(value);
    return isNaN(num) ? '–' : formatarValorMonetario(num);
  };

  const isProposalNaoAtende = (proposal) =>
    !!proposal && proposal.status === "Não atende";

  const getReliabilityColor = (reliability) => {
    const map = {
      Alta: "success",
      Media: "warning",
      Baixa: "error"
    };
    return map[reliability] || "grey";
  };

  const getBadgeConfig = (badgeName) => {
    const map = {
      "Abaixo do ref.": { icon: "mdi-trending-down", color: "success" },
      "Acima do teto": { icon: "mdi-alert-circle-outline", color: "red" },
      "Menor preço": { icon: "mdi-shield-check-outline", color: "success" },
      "Mais rápido": { icon: "mdi-speedometer", color: "success" },
      "Melhor vs prazo desejado": { icon: "mdi-timer-sand", color: "success" },
      "Pior vs prazo desejado": { icon: "mdi-timer-sand", color: "warning" },
      "Melhor vs último prazo": { icon: "mdi-truck-delivery-outline", color: "success" },
      "Pior vs último prazo": { icon: "mdi-truck-delivery-outline", color: "warning" },
      "Mais barato": { icon: "mdi-cash-check", color: "success" },
      "Melhor vs último preço": { icon: "mdi-tag-check-outline", color: "success" },
      "Pior vs último preço": { icon: "mdi-tag-minus-outline", color: "warning" },
      "Fora da tolerância de qtd.": { icon: "mdi-package-variant-closed-remove", color: "warning" },
      "Qtd. fora da tolerância": { icon: "mdi-package-variant-closed-remove", color: "warning" },
      "Qtd. igual ao desejado": { icon: "mdi-check-circle-outline", color: "success" },
    };
    // "Acima do teto (CIF)" e "Acima do teto (FOB)" usam a mesma config (vermelho) que "Acima do teto"
    const key = (badgeName && badgeName.indexOf("Acima do teto") === 0) ? "Acima do teto" : badgeName;
    return map[key] || { icon: "mdi-shield-check-outline", color: "success" };
  };

  /**
   * Selo da quantidade ofertada vs faixa do comprador (cotacao_produtos.qtd_minima / qtd_maxima aceita emb).
   * Prioridade: fora da faixa; depois igual à qtd. pedida.
   */
  const getSeloQuantidadeOfertada = (proposal, item) => {
    if (!proposal || !item) return null;
    if (isProposalNaoAtende(proposal)) return null;
    const raw = proposal.offeredQuantity;
    if (raw === null || raw === undefined || String(raw).trim() === "") return null;
    const q = Number(raw);
    if (isNaN(q)) return null;

    const minRaw = item.minQtyAcceptedEmb;
    const maxRaw = item.maxQtyAcceptedEmb;
    const hasMin =
      minRaw !== null &&
      minRaw !== undefined &&
      minRaw !== "" &&
      !isNaN(Number(minRaw));
    const hasMax =
      maxRaw !== null &&
      maxRaw !== undefined &&
      maxRaw !== "" &&
      !isNaN(Number(maxRaw));
    const lo = hasMin ? Number(minRaw) : null;
    const hi = hasMax ? Number(maxRaw) : null;
    const hasRange = hasMin || hasMax;

    let fora = false;
    if (hasMin && q < lo) fora = true;
    if (hasMax && q > hi) fora = true;

    if (hasRange && fora) {
      let refTexto = "";
      if (hasMin && hasMax) refTexto = `Entre ${lo} e ${hi} (mín./máx. aceitos na embalagem).`;
      else if (hasMin) refTexto = `Mínimo aceito: ${lo}.`;
      else refTexto = `Máximo aceito: ${hi}.`;
      const descricao =
        "A quantidade ofertada está fora dos limites configurados pelo comprador para este item. " +
        refTexto;
      return {
        nomeColQuantidade: "Qtd. fora da tolerância",
        nomeColFornecedor: "Fora da tolerância de qtd.",
        descricao,
      };
    }

    const pedida =
      item.desiredQuantity !== null && item.desiredQuantity !== undefined && item.desiredQuantity !== ""
        ? Number(item.desiredQuantity)
        : null;
    if (pedida != null && !isNaN(pedida) && Math.abs(q - pedida) < 1e-6) {
      return {
        nome: "Qtd. igual ao desejado",
        descricao:
          "A quantidade ofertada coincide com a quantidade pedida na cotação (qtd. pedida).",
      };
    }

    return null;
  };

  /** Uma chamada por linha: selo de qtd. ofertada + tooltip + ícone (evita várias avaliações no template). */
  const getSeloQuantidadeOfertadaContexto = (proposal) => {
    const s = getSeloQuantidadeOfertada(proposal, currentItem.value);
    if (!s) return null;
    const nome = s.nomeColQuantidade || s.nome;
    return {
      nome,
      tooltip: getSeloTooltip(nome, s.descricao),
      badge: getBadgeConfig(nome),
    };
  };

  /** Selo apenas na coluna Fornecedor / proposta: texto distinto do selo na coluna Quantidade ofertada. */
  const getSeloQuantidadeOfertadaContextoFornecedor = (proposal) => {
    const s = getSeloQuantidadeOfertada(proposal, currentItem.value);
    if (!s || !s.nomeColFornecedor) return null;
    const nome = s.nomeColFornecedor;
    return {
      nome,
      tooltip: getSeloTooltip(nome, s.descricao),
      badge: getBadgeConfig(nome),
    };
  };

  const deveExibirStatusPropostaFornecedor = (proposal) => {
    if (!proposal || !proposal.status) return false;
    if (getSeloQuantidadeOfertadaContextoFornecedor(proposal) && proposal.status !== "Não atende") {
      return false;
    }
    return true;
  };

  /** Lista { nome, descricao } usada na visão Por item (badgeList ou legado badges[]). */
  const normalizarSelosPropostaItem = (proposal) => {
    if (!proposal) return [];
    if (isProposalNaoAtende(proposal)) return [];
    if (proposal.badgeList && proposal.badgeList.length) {
      return proposal.badgeList;
    }
    return (proposal.badges || []).map((n) => ({
      nome: n,
      descricao: getSeloTooltip(n, null),
    }));
  };

  /**
   * Distribui selos da visão Por item nas colunas (sem coluna Selos dedicada).
   * Um único passe pela lista de indicadores.
   */
  const selosDistribuidosItemView = (proposal) => {
    if (isProposalNaoAtende(proposal)) {
      return {
        precoUnitario: [],
        variacaoVsRef: [],
        prazoEntrega: [],
      };
    }
    const all = normalizarSelosPropostaItem(proposal);
    const prazoNomes = new Set([
      "Mais rápido",
      "Melhor vs prazo desejado",
      "Pior vs prazo desejado",
    ]);
    const out = {
      precoUnitario: [],
      variacaoVsRef: [],
      prazoEntrega: [],
    };
    for (let i = 0; i < all.length; i++) {
      const b = all[i];
      const n = b.nome || "";
      if (n === "Menor preço" || n.indexOf("Acima do teto") === 0) {
        out.precoUnitario.push(b);
      } else if (n === "Abaixo do ref.") {
        out.variacaoVsRef.push(b);
      } else if (prazoNomes.has(n)) {
        out.prazoEntrega.push(b);
      }
    }
    return out;
  };

  const clearWinners = async () => {
    try {
      if (!props.cotacaoId) {
        toast.error("ID da cotação não informado.");
        return;
      }

      const { data } = await api.post(`/cotacao/analise_proposta/retirar_ganhador/${props.cotacaoId}`);

      if (data.success) {
        toast.success("Ganhadores removidos com sucesso!");

        items.value.forEach((item) => {
          item.proposals.forEach((proposal) => {
            proposal.isWinner = false;
          });
        });

        items.value = [...items.value];

        // Recarregar dados da API
        await fetchAnaliseProposta();
        await fetchAnalisePropostaPorFornecedor();
      } else {
        toast.error(data.message || "Erro ao remover ganhadores.");
      }
    } catch (error) {

      toast.error("Ocorreu um erro ao limpar os ganhadores.");
    }
  };

  const clearFilters = () => {
    // Resetar seleções rápidas
    quickSelection.value = null;

    // Resetar filtros
    selectedSupplier.value = "todos";
    maxPrice.value = "";
    maxDelivery.value = "";
    selectedFreightType.value = "todos";
    selectedReliability.value = null;
    minCoverage.value = "";

    // Resetar checkboxes
    showItemsWithoutWinner.value = false;
    hideOutOfTolerance.value = false;
    hideAboveReference.value = false;
  };

  const removeFilter = (filterName) => {
    // Remover filtros específicos baseado no nome
    if (filterName === "Menor custo total") quickSelection.value = null;
    if (filterName === "Melhor prazo de entrega") quickSelection.value = null;



    if (filterName === "Confiabilidade Alta") selectedReliability.value = null;
    if (filterName === "Confiabilidade Média") selectedReliability.value = null;
    if (filterName === "Confiabilidade Baixa") selectedReliability.value = null;
    if (filterName === "Sem histórico") selectedReliability.value = null;

    if (filterName === "Apenas itens sem ganhador") showItemsWithoutWinner.value = false;

    if (filterName.startsWith("Fornecedor:")) selectedSupplier.value = "todos";
    if (filterName.startsWith("Frete:")) selectedFreightType.value = "todos";
    if (filterName.startsWith("Preço máx:")) maxPrice.value = "";
    if (filterName.startsWith("Prazo máx:")) maxDelivery.value = "";
    if (filterName.startsWith("Cobertura mín:")) minCoverage.value = "";
    if (filterName === "Ocultar fora da tolerância") hideOutOfTolerance.value = false;
    if (filterName === "Ocultar acima de 20% do ref.") hideAboveReference.value = false;
  };

  const previousItem = () => {
    currentIndex.value = Math.max(0, currentIndex.value - 1);
  };

  const nextItem = () => {
    currentIndex.value = Math.min(filteredItems.value.length - 1, currentIndex.value + 1);
  };

  const setWinner = (itemId, proposalId) => {
    const item = items.value.find((i) => i.id === itemId);
    if (!item) return;

    // Verificar se o item já é o ganhador
    const currentWinner = item.proposals.find(p => p.isWinner);
    const isTogglingOff = currentWinner && currentWinner.id === proposalId;

    item.proposals.forEach((proposal) => {
      // Se estiver clicando no que já é ganhador, remove (false)
      // Se não, define como ganhador apenas o clicado
      if (isTogglingOff) {
        proposal.isWinner = false;
      } else {
        proposal.isWinner = (proposal.id === proposalId);
      }
    });

    items.value = [...items.value];

    if (isTogglingOff) {
      toast.info("Ganhador removido.");
    } else {
      // Encontrar nome do novo ganhador para feedback
      const newWinner = item.proposals.find(p => p.id === proposalId);
      const supplierName = newWinner ? newWinner.supplierName : 'fornecedor';
      toast.success(`Definido ${supplierName} como ganhador.`);
    }
  };

  const setWinnerBySupplier = (supplierName, options = {}) => {
    if (!supplierName) return;

    const totalItemsCount = items.value.length;

    let allSelected = true;
    let foundAny = false;
    let totalAtende = 0;

    items.value.forEach(item => {
      const proposal = item.proposals.find(p => p.supplierName === supplierName);
      if (proposal && proposal.status === "Válida") {
        foundAny = true;
        totalAtende++;
        if (!proposal.isWinner) {
          allSelected = false;
        }
      }
    });

    const currentWinsBySupplier = {};
    items.value.forEach(item => {
      const winner = item.proposals.find(p => p.isWinner);
      if (winner) {
        const name = winner.supplierName;
        currentWinsBySupplier[name] = (currentWinsBySupplier[name] || 0) + 1;
      }
    });

    let partialWinnerName = null;
    if (suppliers.value.length > 0) {
      const found = suppliers.value.find(s2 => {
        if (s2.supplierName === supplierName) return false;
        const wins = currentWinsBySupplier[s2.supplierName] || 0;
        const itemsTotalS2 = s2.itemsTotal != null ? s2.itemsTotal : 0;
        const notAttended = totalItemsCount - itemsTotalS2;
        return notAttended > 0 && wins > 0;
      });
      if (found) partialWinnerName = found.supplierName;
    }

    const thisSupplierFromApi = suppliers.value.find(s => s.supplierName === supplierName);
    const thisItemsTotal = thisSupplierFromApi && (thisSupplierFromApi.itemsTotal != null) ? thisSupplierFromApi.itemsTotal : totalAtende;
    const thisNotAttended = Math.max(0, totalItemsCount - thisItemsTotal);
    const thisIsPartialWinner = thisNotAttended > 0 && allSelected;
    const thisIsWinnerRemainingItems = partialWinnerName && !thisIsPartialWinner && (currentWinsBySupplier[supplierName] || 0) > 0;

    // Desmarcar: quando está ganhador em todos os itens que atende OU quando é "ganhador itens restantes" (tem pelo menos uma vitória)
    const isTogglingOff = (foundAny && allSelected) || thisIsWinnerRemainingItems;

    // Calcular onlyPendingItems com estado ATUAL no momento do clique (evita closure desatualizada)
    let onlyPendingItems = options.onlyPendingItems === true;
    if (!isTogglingOff && suppliers.value.length > 0) {
      onlyPendingItems = !!partialWinnerName && !thisIsPartialWinner;
    }

    if (isTogglingOff && thisIsPartialWinner) {
      // Desmarcar ganhador parcial = limpar todos os ganhadores (como "Limpar ganhadores")
      items.value.forEach(item => {
        item.proposals.forEach(p => { p.isWinner = false; });
      });
    } else {
      items.value.forEach(item => {
        const proposal = item.proposals.find(p => p.supplierName === supplierName);
        if (proposal && proposal.status === "Válida") {
          if (isTogglingOff) {
            proposal.isWinner = false;
          } else {
            const itemAlreadyHasWinner = item.proposals.some(p => p.isWinner);
            if (onlyPendingItems && itemAlreadyHasWinner) {
              return;
            }
            item.proposals.forEach(p => p.isWinner = false);
            proposal.isWinner = true;
          }
        }
      });
    }

    items.value = [...items.value];

    if (isTogglingOff) {
      if (thisIsPartialWinner) {
        toast.info("Ganhador parcial desmarcado. Todos os ganhadores foram removidos.");
      } else if (thisIsWinnerRemainingItems) {
        toast.info(`Vínculo de ganhador removido dos itens restantes: ${supplierName}`);
      } else {
        toast.info(`Vínculo de ganhador removido para os ${totalAtende} itens que atende: ${supplierName}`);
      }
    } else if (onlyPendingItems) {
      toast.success(`Definido ${supplierName} como ganhador nos itens restantes.`);
    } else {
      toast.success(`Definido ${supplierName} como ganhador para os ${totalAtende} itens que atende.`);
    }
  };

  const modalConfirmacao = ref(false);

  const confirmSuppliers = () => {
    const hasWinner = items.value.some(item => item.proposals.some(p => p.isWinner));
    if (!hasWinner) {
      toast.warning("Selecione ao menos um ganhador para concluir a análise.");
      return;
    }
    modalConfirmacao.value = true;
  };

  const executeConfirmSuppliers = async () => {
    try {
      const ganhadores = [];

      items.value.forEach((item) => {
        const winner = item.proposals.find((p) => p.isWinner);
        if (winner) {
          ganhadores.push({
            cotacao_transacao_id: props.cotacaoId,
            cotacao_produtos_id: item.id,
            cotacao_fornecedores_participantes_id: winner.id,
          });
        }
      });

      modalConfirmacao.value = false;

      const { data } = await api.post(`/cotacao/analise_proposta/sincronizar_ganhadores/${props.cotacaoId}`, {
        ganhadores,
      });

      if (data.success) {
        toast.success("Fornecedores confirmados e análise concluída com sucesso!");
        await fetchAnaliseProposta();
        await fetchAnalisePropostaPorFornecedor();

        if (context && context.emit) {
          context.emit('refresh');
        }
      } else {
        toast.error(data.message || "Erro ao confirmar fornecedores.");
      }
    } catch (error) {
      toast.error("Ocorreu um erro ao confirmar os fornecedores.");
    }
  };

  const modalItensGanhadores = ref(false);
  const modalFornecedor = ref("todos");
  const modalSoloSemGanhador = ref(false);

  const modalItensFornecedor = ref(false);
  const modalItensFornecedorNome = ref("");
  const modalItensFornecedorTipo = ref("atendidos"); // atendidos | nao_atendidos | ganhos

  const modalItensFornecedorRows = computed(() => {
    const supplierName = (modalItensFornecedorNome.value || "").toString();
    const tipo = (modalItensFornecedorTipo.value || "atendidos").toString();
    const mostrarPrecoETotal = tipo !== "nao_atendidos";

    return filteredItems.value
      .map((item) => {
        const proposal = item.proposals.find((p) => (p && p.supplierName) === supplierName);
        const winner = item.proposals.find((p) => p.isWinner);
        const qtdNum = Number(item.desiredQuantity) || 0;
        const unit =
          mostrarPrecoETotal && proposal ? parseValue(proposal.unitPrice) : NaN;
        const totalDoItemValor =
          mostrarPrecoETotal && Number.isFinite(unit) ? unit * qtdNum : 0;
        return {
          id: item.id,
          codigo: item.code,
          descricao: item.productName,
          qtd: item.desiredQuantity,
          un: item.unit,
          preco:
            mostrarPrecoETotal && proposal && proposal.unitPrice
              ? proposal.unitPrice
              : null,
          totalDoItemValor,
          totalDoItem:
            mostrarPrecoETotal && Number.isFinite(unit)
              ? formatarValorMonetario(totalDoItemValor)
              : null,
          // states
          atende: !!proposal && proposal.status === "Válida",
          temProposta: !!proposal,
          ganhou: !!winner && winner.supplierName === supplierName,
        };
      })
      .filter((row) => {
        if (tipo === "ganhos") return row.ganhou;
        if (tipo === "nao_atendidos") return !row.atende;
        return row.atende; // atendidos
      });
  });

  const modalItensFornecedorTotalGeral = computed(() => {
    const total = modalItensFornecedorRows.value.reduce(
      (acc, row) => acc + (Number(row.totalDoItemValor) || 0),
      0
    );
    return formatarValorMonetario(total);
  });

  const openModalItensFornecedor = (supplierName, tipo) => {
    modalItensFornecedorNome.value = (supplierName || "").toString();
    modalItensFornecedorTipo.value = (tipo || "atendidos").toString();
    modalItensFornecedor.value = true;
  };

  const closeModalItensFornecedor = () => {
    modalItensFornecedor.value = false;
  };

  const baixarXlsxModalItensFornecedor = () => {
    const supplierName = (modalItensFornecedorNome.value || "").toString();
    const tipo = (modalItensFornecedorTipo.value || "atendidos").toString();
    const rows = modalItensFornecedorRows.value;

    if (!supplierName) {
      toast.warning("Não foi possível exportar: fornecedor não identificado.");
      return;
    }
    if (!rows.length) {
      toast.warning("Não há linhas para exportar.");
      return;
    }

    const incluirPreco = tipo !== "nao_atendidos";
    const headers = incluirPreco
      ? ["Item", "Descrição", "Qtd.", "Un.", "Preço", "Total do item"]
      : ["Item", "Descrição", "Qtd.", "Un."];
    const dataRows = rows.map((row) => {
      const item = row.codigo != null && row.codigo !== "" ? row.codigo : row.id;
      const base = [
        item,
        row.descricao || "",
        formatQuantity(row.qtd),
        row.un || "–",
      ];
      if (incluirPreco) {
        base.push(row.preco || "–", row.totalDoItem || "–");
      }
      return base;
    });

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
    const workbook = XLSX.utils.book_new();
    const sheetLabel =
      tipo === "ganhos"
        ? "Itens ganhos"
        : tipo === "nao_atendidos"
          ? "Não atendidos"
          : "Itens atendidos";
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetLabel.slice(0, 31));

    const safeName = supplierName.replace(/[^\w\s()-]/g, "").trim().replace(/\s+/g, "_");
    const fileName = `ITENS_${tipo.toUpperCase().replace(/_/g, "-")}_${safeName || "FORNECEDOR"}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const modalItensRows = computed(() => {
    let rows = items.value.map((item) => {
      const winner = item.proposals.find((p) => p.isWinner);
      const qtdNum = Number(item.desiredQuantity) || 0;
      const unit = winner ? parseValue(winner.unitPrice) : NaN;
      const totalDoItemValor =
        winner && Number.isFinite(unit)
          ? unit * qtdNum
          : 0;
      const totalDoItem =
        winner && Number.isFinite(unit)
          ? formatarValorMonetario(totalDoItemValor)
          : null;
      return {
        id: item.id,
        codigo: item.code,
        descricao: item.productName,
        qtd: item.desiredQuantity,
        un: item.unit,
        preco: winner ? winner.unitPrice : null,
        totalDoItemValor,
        totalDoItem,
        ganhador: winner ? winner.supplierName : null,
      };
    });
    if (modalSoloSemGanhador.value) {
      rows = rows.filter((r) => !r.ganhador);
    } else if (modalFornecedor.value && modalFornecedor.value !== "todos") {
      const sel = supplierOptions.value.find((s) => s.value === modalFornecedor.value);
      const name = sel ? sel.text : "";
      rows = rows.filter(
        (r) => r.ganhador && r.ganhador.toLowerCase() === String(name).toLowerCase()
      );
    }
    return rows;
  });

  const modalItensTotalGeral = computed(() => {
    const total = modalItensRows.value.reduce(
      (acc, row) => acc + (Number(row.totalDoItemValor) || 0),
      0
    );
    return formatarValorMonetario(total);
  });

  const openModalItensGanhadores = () => {
    modalFornecedor.value = "todos";
    modalSoloSemGanhador.value = false;
    modalItensGanhadores.value = true;
  };

  const closeModalItensGanhadores = () => {
    modalItensGanhadores.value = false;
  };

  watch(modalSoloSemGanhador, (v) => {
    if (v) {
      modalFornecedor.value = "todos";
    }
  });

  const saveInAnalysis = async () => {
    try {
      const ganhadores = [];

      items.value.forEach((item) => {
        const winner = item.proposals.find((p) => p.isWinner);
        if (winner) {
          ganhadores.push({
            cotacao_transacao_id: props.cotacaoId,
            cotacao_produtos_id: item.id,
            cotacao_fornecedores_participantes_id: winner.id,
          });
        }
      });

      if (ganhadores.length === 0) {
        toast.warning("Selecione ao menos um ganhador para salvar.");
        return;
      }

      const payload = {
        ganhadores: ganhadores
      };

      const { data } = await api.post("/cotacao/analise_proposta/definir_ganhador", payload);

      if (data.success) {
        toast.success("Análise salva com sucesso!");
        // Opcional: Recarregar dados para garantir sincronia com backend
        await fetchAnaliseProposta();
        await fetchAnalisePropostaPorFornecedor();
      } else {
        toast.error(data.message || "Erro ao salvar análise.");
      }
    } catch (error) {

      toast.error("Ocorreu um erro ao salvar a análise.");
    }
  };

  // ─── Modal "Solicitar ajuste de proposta" ────────────────────────────
  const modalAjusteAberto = ref(false);
  const modalAjusteFornecedorSelecionado = ref(null);
  const modalAjustePrazoDias = ref(null);
  const modalAjusteLoading = ref(false);
  const modalAjusteContatos = ref([]);
  const modalAjusteContatosLoading = ref(false);
  const modalAjusteErros = ref({ fornecedor: false, prazo: false });
  const ajustesAtivos = ref([]);

  const fornecedoresComProposta = computed(() => {
    const map = {};
    for (const item of items.value) {
      for (const p of item.proposals || []) {
        if (p.supplierName && p.supplierId && !map[p.supplierId]) {
          map[p.supplierId] = {
            value: p.supplierId,
            text: p.supplierName,
            cnpj: p.cnpj || '',
          };
        }
      }
    }
    for (const s of suppliers.value) {
      if (s.supplierName && s.supplierId && !map[s.supplierId]) {
        map[s.supplierId] = {
          value: s.supplierId,
          text: s.supplierName,
          cnpj: s.cnpj || '',
        };
      }
    }
    return Object.values(map);
  });

  const modalAjusteFornecedorExibicao = computed(() => {
    var id = modalAjusteFornecedorSelecionado.value;
    if (id == null || id === "") {
      return { nome: "", cnpj: "" };
    }
    var fromList = fornecedoresComProposta.value.find(function (x) {
      return x.value === id || String(x.value) === String(id);
    });
    if (fromList) {
      return { nome: fromList.text || "", cnpj: fromList.cnpj || "" };
    }
    var i;
    var j;
    for (i = 0; i < items.value.length; i++) {
      var proposals = items.value[i].proposals || [];
      for (j = 0; j < proposals.length; j++) {
        var p = proposals[j];
        if (p.supplierId === id || String(p.supplierId) === String(id)) {
          return { nome: p.supplierName || "", cnpj: p.cnpj || "" };
        }
      }
    }
    for (i = 0; i < suppliers.value.length; i++) {
      var s = suppliers.value[i];
      if (s.supplierId === id || String(s.supplierId) === String(id)) {
        return { nome: s.supplierName || "", cnpj: s.cnpj || "" };
      }
    }
    return { nome: "Fornecedor (ID " + id + ")", cnpj: "" };
  });

  const modalAjusteMaxDias = computed(() => {
    if (!terminoPeriodoRecebimento.value) return null;
    var termino = parseDate(terminoPeriodoRecebimento.value);
    if (!termino) return null;
    var diffMs = termino.getTime() - Date.now();
    if (diffMs <= 0) return 0;
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  });

  const terminoCotacaoFormatado = computed(() => {
    if (!terminoPeriodoRecebimento.value) return null;
    var termino = parseDate(terminoPeriodoRecebimento.value);
    if (!termino) return null;
    var dd = String(termino.getDate()).padStart(2, '0');
    var mm = String(termino.getMonth() + 1).padStart(2, '0');
    var yyyy = termino.getFullYear();
    var hh = String(termino.getHours()).padStart(2, '0');
    var min = String(termino.getMinutes()).padStart(2, '0');
    return dd + '/' + mm + '/' + yyyy + ', ' + hh + ':' + min;
  });

  const modalAjustePrazoFinal = computed(() => {
    var dias = parseInt(modalAjustePrazoDias.value);
    if (!dias || dias <= 0) return null;
    var agora = new Date();
    agora.setDate(agora.getDate() + dias);
    var dd = String(agora.getDate()).padStart(2, '0');
    var mm = String(agora.getMonth() + 1).padStart(2, '0');
    var yyyy = agora.getFullYear();
    var hh = String(agora.getHours()).padStart(2, '0');
    var min = String(agora.getMinutes()).padStart(2, '0');
    return dd + '/' + mm + '/' + yyyy + ', ' + hh + ':' + min;
  });

  const modalAjustePrazoExcedido = computed(() => {
    if (!terminoPeriodoRecebimento.value || !modalAjustePrazoDias.value) return false;
    var dias = parseInt(modalAjustePrazoDias.value);
    if (!dias || dias <= 0) return false;
    var termino = parseDate(terminoPeriodoRecebimento.value);
    if (!termino) return false;
    var prazoFinalMs = Date.now() + (dias * 24 * 60 * 60 * 1000);
    return prazoFinalMs > termino.getTime();
  });

  const buscarContatosFornecedor = async (fornecedorId) => {
    if (!fornecedorId || !props.cotacaoId) {
      modalAjusteContatos.value = [];
      return;
    }
    modalAjusteContatosLoading.value = true;
    try {
      var res = await api.get("/cotacao/analise_proposta/contatos_fornecedor/" + props.cotacaoId + "/" + fornecedorId);
      if (res.data && res.data.success) {
        modalAjusteContatos.value = res.data.contatos || [];
      } else {
        modalAjusteContatos.value = [];
      }
    } catch (err) {
      modalAjusteContatos.value = [];
    } finally {
      modalAjusteContatosLoading.value = false;
    }
  };

  watch(() => modalAjusteFornecedorSelecionado.value, (newVal) => {
    modalAjusteErros.value.fornecedor = false;
    if (newVal) {
      buscarContatosFornecedor(newVal);
    } else {
      modalAjusteContatos.value = [];
    }
  });

  watch(() => modalAjustePrazoDias.value, () => {
    modalAjusteErros.value.prazo = false;
  });

  const abrirModalAjuste = (fornecedorId) => {
    modalAjusteFornecedorSelecionado.value = fornecedorId || null;
    modalAjustePrazoDias.value = null;
    modalAjusteContatos.value = [];
    modalAjusteErros.value = { fornecedor: false, prazo: false };
    modalAjusteAberto.value = true;
    if (fornecedorId) {
      buscarContatosFornecedor(fornecedorId);
    }
  };

  const fecharModalAjuste = () => {
    modalAjusteAberto.value = false;
    modalAjusteFornecedorSelecionado.value = null;
    modalAjustePrazoDias.value = null;
    modalAjusteContatos.value = [];
    modalAjusteErros.value = { fornecedor: false, prazo: false };
  };

  const validarModalAjuste = () => {
    var valido = true;
    if (!modalAjusteFornecedorSelecionado.value) {
      modalAjusteErros.value.fornecedor = true;
      valido = false;
    }
    var dias = parseInt(modalAjustePrazoDias.value);
    if (!dias || dias <= 0) {
      modalAjusteErros.value.prazo = true;
      valido = false;
    }
    if (modalAjustePrazoExcedido.value) {
      modalAjusteErros.value.prazo = true;
      valido = false;
    }
    return valido;
  };

  const enviarSolicitacaoAjuste = async () => {
    if (!validarModalAjuste()) return;

    modalAjusteLoading.value = true;
    try {
      var res = await api.post("/cotacao/analise_proposta/solicitar_ajuste", {
        cotacao_transacao_id: props.cotacaoId,
        fornecedor_id: modalAjusteFornecedorSelecionado.value,
        prazo_dias: parseInt(modalAjustePrazoDias.value),
      });

      if (res.data && res.data.success) {
        toast.success(res.data.message || "Ajuste solicitado com sucesso.");
        fecharModalAjuste();
        await carregarAjustesAtivos();
      } else {
        toast.error(res.data.message || "Erro ao solicitar ajuste.");
      }
    } catch (err) {
      var msg = (err && err.response && err.response.data && err.response.data.message) || "Erro ao solicitar ajuste.";
      toast.error(msg);
    } finally {
      modalAjusteLoading.value = false;
    }
  };

  const carregarAjustesAtivos = async () => {
    try {
      var res = await api.get("/cotacao/analise_proposta/ajustes_ativos/" + props.cotacaoId);
      if (res.data && res.data.success) {
        ajustesAtivos.value = res.data.ajustes || [];
      }
    } catch (err) {
      // silencioso
    }
  };

  const getAjusteAtivoFornecedor = (fornecedorId) => {
    return ajustesAtivos.value.find(
      function (a) { return a.fornecedor_id === fornecedorId && a.status === 'solicitado'; }
    );
  };

  const formatarDataBR = (dataStr) => {
    if (!dataStr) return '–';
    var d = parseDate(dataStr);
    if (!d) return dataStr;
    var dd = String(d.getDate()).padStart(2, '0');
    var mm = String(d.getMonth() + 1).padStart(2, '0');
    var yyyy = d.getFullYear();
    var hh = String(d.getHours()).padStart(2, '0');
    var min = String(d.getMinutes()).padStart(2, '0');
    return dd + '/' + mm + '/' + yyyy + ', ' + hh + ':' + min;
  };

  return {
    viewType,
    showItemsWithoutWinner,
    quickSelection,
    selectedSupplier,
    maxPrice,
    maxDelivery,
    selectedFreightType,
    freightTypeOptions,
    isQuickSelectionExpanded,
    isFiltersExpanded,
    toggleCard,
    supplierOptions,
    itemsWithWinner,
    totalItems,
    itemsWithoutWinner,
    hasItensRestantesSemGanhador,
    winnerDistribution,
    totalCost,
    winningSuppliers,
    mainSupplier,
    hasProposals,
    items,
    prazoEntregaReferenciaDias,
    currentIndex,
    currentItem,
    suppliersView,
    filteredSuppliersView,
    hasPartialWinner,
    activeFilters,
    minCoverage,
    hideOutOfTolerance,
    hideAboveReference,
    isAdvancedOptionsExpanded,
    isButtonsDisabled,
    formatCurrency: formatarValorMonetario,
    formatQuantity,
    formatLastPurchasePrice,
    isProposalNaoAtende,
    getReliabilityColor,
    getBadgeConfig,
    getSeloTooltip,
    normalizarSelosPropostaItem,
    selosDistribuidosItemView,
    getSeloQuantidadeOfertada,
    getSeloQuantidadeOfertadaContexto,
    getSeloQuantidadeOfertadaContextoFornecedor,
    deveExibirStatusPropostaFornecedor,
    clearWinners,
    clearFilters,
    removeFilter,
    previousItem,
    nextItem,
    setWinner,
    setWinnerBySupplier,
    confirmSuppliers,
    executeConfirmSuppliers,
    modalConfirmacao,
    saveInAnalysis,
    filteredItems,
    modalItensGanhadores,
    modalFornecedor,
    modalSoloSemGanhador,
    modalItensRows,
    modalItensTotalGeral,
    openModalItensGanhadores,
    closeModalItensGanhadores,
    modalItensFornecedor,
    modalItensFornecedorNome,
    modalItensFornecedorTipo,
    modalItensFornecedorRows,
    modalItensFornecedorTotalGeral,
    openModalItensFornecedor,
    closeModalItensFornecedor,
    baixarXlsxModalItensFornecedor,
    // Ajuste de proposta
    modalAjusteAberto,
    modalAjusteFornecedorSelecionado,
    modalAjustePrazoDias,
    modalAjusteLoading,
    modalAjusteContatos,
    modalAjusteContatosLoading,
    modalAjusteErros,
    modalAjustePrazoFinal,
    modalAjustePrazoExcedido,
    modalAjusteMaxDias,
    terminoCotacaoFormatado,
    ajustesAtivos,
    fornecedoresComProposta,
    modalAjusteFornecedorExibicao,
    abrirModalAjuste,
    fecharModalAjuste,
    enviarSolicitacaoAjuste,
    carregarAjustesAtivos,
    getAjusteAtivoFornecedor,
    formatarDataBR,
  };
}
